import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConnectionService } from '@services/connection-service';
import { HairVariantServices, VariantServices } from 'e2e/src/shared/variant-services';
import { AppConfig } from 'client/app/app.config';
import { extractRegimenProducts } from 'client/helper/regimen-helper';
import { Experiment, Regimen, RegimenVariant, User } from '@cureskin/api-client/src/server-api';
import { PlanName, VariantId, selectedPlanAndVariantIdMap, RegimenService, planName } from '@services/regimen-service';
import { getPlanInfoConfig, PlanInfoConfig, planInfoItemId } from './choose-plan-info.constants';
import { EventLoggerService } from '@services/event-logger-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { ApiClientConstant } from '@cureskin/api-client';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { ToolbarTabsComponent } from '@components/toolbar-tabs/toolbar-tabs.component';
import { SwiperComponent } from 'swiper_angular';
import { SwiperOptions } from 'swiper';
import { UserAllocatedDoctorService } from '@services/user-allocated-doctor-service/user-allocated-doctor.service';

const VARIANT_ID = {
  variant_999: 'variant_999',
  variant_4999: 'variant_4999',
};

@Component({
  selector: 'choose-plan-info',
  templateUrl: './choose-plan-info.html',
  styleUrls: [],
  standalone: false,
})

export class ChoosePlanInfoComponent implements OnDestroy {
  tabData: any = { count: 3, selectedTab: 0 };
  planInfo: Array<PlanName> = ['advance', 'pro', 'assist'];
  regimenClass: any = 'FACE';
  private _planInfoConfigCache: { data: Record<PlanName, PlanInfoConfig>; key: string } | null = null;
  private readonly _servicesCache: Map<string, any[]> = new Map();
  regimenPlan: any = 'advance';
  variantServices: any = VariantServices;
  hairVariants: any = HairVariantServices;
  isStandAlone: boolean = false;
  loading: boolean = false;
  updatingPlan: boolean = false;

  @ViewChild('toolbarTabs', { static: false }) toolbarTabs: ToolbarTabsComponent;
  @ViewChild('planSwiper', { static: false }) planSwiper: SwiperComponent;
  planSwiperConfig: SwiperOptions = {
    slidesPerView: 1,
    spaceBetween: 0,
    allowTouchMove: true,
  };
  @Input() selectedPlan: PlanName = 'advance';
  user: any;
  userLanguage: string;
  upsellPlans: Array<PlanName> = ['introductory', 'advance', 'pro', 'assist'];
  standAlonePlans: Array<PlanName> = ['introductory', 'advance'];
  regimenCoverImage: string = '';
  variantsInfo: Array<RegimenVariant> = [];
  isRegimenNewPropositionUI: boolean = false;
  optedForDoctorCall: boolean = false;
  regimenNewPropositionData: {
    regimenId: string,
    variantId: VariantId,
    totalSellingPrice: number,
    productsCount: number,
    doctorImageUrl: string
  } = {
      regimenId: '',
      variantId: '' as VariantId,
      totalSellingPrice: 0,
      productsCount: 0,
      doctorImageUrl: '',
    };
  experiments: Array<Experiment>;
  regimenPrice: number = 1499;
  selectedVariant: RegimenVariant;
  regimens: Array<Regimen> = [];
  selectedRegimen: Regimen | null = null;
  allocatedDoctor: User = null;
  doctorImageUrl: string = '';
  planInfoItemId = planInfoItemId;
  moneyBackGuaranteeExperiment: Experiment | null = null;
  planCoverImages: string[] = [];
  showPlanSelectedToast: boolean = false;
  isToastDismissing: boolean = false;
  toastPlanPrice: number = 0;
  private toastTimeoutId: ReturnType<typeof setTimeout> | null = null;
  constructor(
    private conn: ConnectionService,
    private route: ActivatedRoute,
    public appConfig: AppConfig,
    private router: Router,
    private readonly regimenService: RegimenService,
    private readonly eventLoggerService: EventLoggerService,
    private readonly experimentService: ExperimentService,
    private readonly appWebBridge: AppWebBridgeService,
    private readonly userAllocatedDoctorService: UserAllocatedDoctorService,
  ) {
    this.doctorImageUrl = this.userAllocatedDoctorService.DEFAULT_DOCTOR_IMAGE_THUMBNAIL;
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.user = this.conn.getActingUser();
    if (this.user.get('allocatedDoctor')) {
      this.allocatedDoctor = await this.conn.findUserByObjectId(this.user.get('allocatedDoctor').id);
      this.doctorImageUrl = this.allocatedDoctor?.get('DoctorImageThumbnail') || this.userAllocatedDoctorService.DEFAULT_DOCTOR_IMAGE_THUMBNAIL;
    }
    await this.setUserExperiments();
    this.route.queryParams.subscribe((params: any): void => {
      this.regimenClass = params.class;
      this.regimenPlan = params.plan;
      this.selectedPlan = this.regimenPlan;
      // Clear services cache when regimen class changes
      this._servicesCache.clear();
      if (params.isStandAlone === 'true') this.isStandAlone = true;
      if (params.isRegimenNewPropositionUI === 'true') this.isRegimenNewPropositionUI = true;
      if (params.optedForDoctorCall === 'true') this.optedForDoctorCall = true;
      if (params.regimenId) this.regimenNewPropositionData.regimenId = params.regimenId;
      if (params.doctorImageUrl) this.regimenNewPropositionData.doctorImageUrl = params.doctorImageUrl;
    });
    this.variantsInfo = await this.getAndProcessVariantsInfo();
    this.setSelectedVariantBasedOnPlan();
    this.userLanguage = this.user.get('languagePreference') || this.appConfig.Shared.Languages.EN;
    if (this.regimenPlan === 'introductory' && this.variantsInfo?.length === 4) {
      if (this.isStandAlone) this.tabData = { count: 2 };
      else this.tabData = { count: 4 };
      this.tabData.selectedTab = 0;
    } else if (this.isStandAlone) {
      this.tabData = { count: 2 };
      this.tabData.selectedTab = this.standAlonePlans.indexOf(this.regimenPlan);
    } else {
      const index = this.planInfo.indexOf(this.regimenPlan);
      this.tabData.selectedTab = index !== -1 ? index : 0;
    }
    if (this.shouldShowRebrandedRegimenProposition) {
      await this.preloadAllCoverImages();
      this.planSwiperConfig = { ...this.planSwiperConfig, initialSlide: this.tabData.selectedTab };
    } else {
      await this.updateRegimenIcons();
    }
    this.updateRegimenPropositionData(this.tabData.selectedTab);
    this.eventLoggerService.cleverTapEvent('pageOpen', JSON.stringify({
      pageName: 'treatment-info-page',
      regimenClass: this.regimenClass,
      regimenId: this.selectedRegimen?.regimenId,
    }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
    ]);
    this.loading = false;
  }

  async setUserExperiments(): Promise<void> {
    this.experiments = await this.conn.findUserActiveExperiments();
    this.experiments.forEach((experiment): void => {
      if (experiment.key === 'money_back_guarantee') {
        this.moneyBackGuaranteeExperiment = experiment;
        // Clear services cache when experiment changes
        this._servicesCache.clear();
      }
    });
  }

  setSelectedVariantBasedOnPlan(): void {
    const variantId = selectedPlanAndVariantIdMap[this.selectedPlan] || selectedPlanAndVariantIdMap.advance;
    this.selectedVariant = this.variantsInfo.find((each: RegimenVariant): boolean => each.variantId === variantId);
  }

  get regimenPriceBasedOnSelectedPlan(): number {
    return this.selectedVariant?.fixedPrice;
  }

  get regimenMrpBasedOnSelectedPlan(): number {
    return this.selectedVariant?.fixedPriceMRP;
  }

  get regimenDiscountBasedOnSelectedPlan(): number {
    if (!this.selectedVariant?.fixedPriceMRP || !this.selectedVariant?.fixedPrice) return 0;
    return Math.floor(((this.selectedVariant.fixedPriceMRP - this.selectedVariant.fixedPrice) * 100) / this.selectedVariant.fixedPriceMRP);
  }

  get activePlans(): PlanName[] {
    if (this.tabData?.count === 4) return this.upsellPlans;
    if (this.tabData?.count === 2) return this.standAlonePlans;
    return this.planInfo;
  }

  getVariantForTabIndex(index: number): RegimenVariant {
    const skipIntro = this.variantsInfo?.length === 4 && this.tabData?.count === 3;
    const variantIndex = skipIntro ? index + 1 : index;
    return this.variantsInfo?.[variantIndex];
  }

  getProductsCountForIndex(index: number): number {
    return extractRegimenProducts(this.getVariantForTabIndex(index))?.length || 0;
  }

  getPlanInfoByPlan(plan: PlanName): PlanInfoConfig {
    const cacheKey = `${this.regimenClass}-${this.moneyBackGuaranteeExperiment?.objectId || 'none'}-${this.experimentService.videoCallExperiment}`;
    if (this._planInfoConfigCache?.key !== cacheKey) {
      const enabledPlans = this.getEnabledPlansForMoneyBackGuarantee();
      this._planInfoConfigCache = {
        data: getPlanInfoConfig(false, enabledPlans, this.experimentService.videoCallExperiment),
        key: cacheKey,
      };
    }
    return this._planInfoConfigCache.data[plan] || this._planInfoConfigCache.data.advance;
  }

  async preloadAllCoverImages(): Promise<void> {
    const promises = this.activePlans.map(async (_, index) => {
      const variant = this.getVariantForTabIndex(index);
      if (!variant?.combinedProductIds) return '';
      const mediaLinkObj = await this.conn.fetchImageFromMediaLink(variant.combinedProductIds);
      return mediaLinkObj[0]?.get('link') || '';
    });
    this.planCoverImages = await Promise.all(promises);
    this.regimenCoverImage = this.planCoverImages[this.tabData?.selectedTab || 0] || '';
  }

  private getEnabledPlansForMoneyBackGuarantee(): PlanName[] {
    return this.regimenService.getEnabledPlansForMoneyBackGuarantee(this.moneyBackGuaranteeExperiment, this.regimenClass, this.user);
  }

  getServicesForPlan(plan: PlanName, isHair: boolean = false): any[] {
    // Create cache key based on plan, isHair, and experiment state
    const cacheKey = `${plan}-${isHair}-${this.regimenClass}-${this.moneyBackGuaranteeExperiment?.objectId || 'none'}`;

    if (this._servicesCache.has(cacheKey)) {
      return this._servicesCache.get(cacheKey)!;
    }

    const services = isHair ? this.hairVariants[plan] : this.variantServices[plan];
    if (!services) {
      this._servicesCache.set(cacheKey, []);
      return [];
    }

    const enabledPlans = this.getEnabledPlansForMoneyBackGuarantee();
    const result = this.regimenService.getServicesForPlan(services, plan, enabledPlans);

    // Cache the result
    this._servicesCache.set(cacheKey, result);
    return result;
  }

  getItemImage(item: any): string {
    if (item.id === planInfoItemId.PRODUCTS && this.regimenCoverImage) {
      return this.regimenCoverImage;
    }
    if ((item.id === planInfoItemId.DEDICATED_DOCTOR || item.id === planInfoItemId.ONE_ON_ONE_DOCTOR) && this.doctorImageUrl) {
      return this.doctorImageUrl;
    }
    return item.imageUrl;
  }

  getVariantPrice(tabIndex: number): number | undefined {
    return this.getVariantForTabIndex(tabIndex)?.fixedPrice;
  }

  get isSelectedVariantIdMatchingPlan(): boolean {
    if (!this.selectedRegimen) return false;
    const selectedVariantId = this.selectedRegimen?.selectedVariantId;

    if (selectedVariantId == null) {
      return this.selectedPlan === planName.advance;
    }

    return selectedVariantId === selectedPlanAndVariantIdMap[this.selectedPlan];
  }

  async updateRegimenIcons(): Promise<void> {
    if (!this.variantsInfo) return;
    // If not introductory plan, skip the first index
    let variantIndex = this.tabData?.selectedTab || 0;
    if (this.regimenPlan !== 'introductory' && this.variantsInfo?.length === 4) variantIndex = this.tabData.selectedTab + 1 || 0;
    const { combinedProductIds }: any = this.variantsInfo[variantIndex];
    const mediaLinkObj = await this.conn.fetchImageFromMediaLink(combinedProductIds);
    this.regimenCoverImage = mediaLinkObj[0]?.get('link');
  }

  async getAndProcessVariantsInfo(): Promise<Array<RegimenVariant>> {
    this.regimens = await this.conn.fetchRegimens(null, true);
    this.selectedRegimen = this.regimens.find((each: Regimen): boolean => each.class === this.regimenClass) || null;
    this.selectedRegimen?.variants.sort((variant1: RegimenVariant, variant2: RegimenVariant): number => variant1.fixedPrice - variant2.fixedPrice);
    return this.selectedRegimen?.variants || [];
  }

  back(): void {
    this.router.navigate(['/user'], {
      queryParams: { tab: 'regimen', class: this.regimenClass || 'FACE', init: 'true' },
      replaceUrl: true,
    });
  }

  async tabChange(index: any): Promise<void> {
    if (this.tabData?.count === 4) {
      this.selectedPlan = this.upsellPlans[index];
    } else if (this.tabData?.count === 2) {
      this.selectedPlan = this.standAlonePlans[index];
    } else {
      this.selectedPlan = this.planInfo[index];
    }
    this.updateRegimenIcons();
    this.setSelectedVariantBasedOnPlan();
    this.syncSwiperToTab(index);

    if (this.isRegimenNewPropositionUI) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          plan: this.selectedPlan,
          optedForDoctorCall: this.optedForDoctorCall,
        },
        queryParamsHandling: 'merge',
      });
      this.updateRegimenPropositionData(index);
      this.updateRegimenKitInfo();
      this.handleMandatoryVariantCall();
    }
  }

  onPlanSlideChange(event: any): void {
    const swiperInstance = event[0];
    const newIndex = swiperInstance.activeIndex;
    if (newIndex !== this.tabData?.selectedTab && this.toolbarTabs) {
      this.toolbarTabs.tabChange(newIndex);
    }
  }

  syncSwiperToTab(index: number): void {
    if (this.planSwiper?.swiperRef && this.planSwiper.swiperRef.activeIndex !== index) {
      this.planSwiper.swiperRef.slideTo(index, 300);
    }
  }

  updateRegimenPropositionData(index: number): void {
    try {
      const variantsInfoIndex = this.regimenPlan !== 'introductory' && this.variantsInfo?.length === 4 ? index + 1 : index;
      this.regimenNewPropositionData.variantId = this.variantsInfo[variantsInfoIndex].variantId as unknown as VariantId;
      this.regimenNewPropositionData.totalSellingPrice = this.variantsInfo[variantsInfoIndex].fixedPrice;
      this.regimenNewPropositionData.productsCount = this.variantsInfo[variantsInfoIndex].products.length;
    } catch (err) {
      // Handle error
    }
  }

  async updateRegimenKitInfo(): Promise<void> {
    try {
      await this.conn.updateRegimenInfo(this.regimenNewPropositionData.regimenId, this.regimenNewPropositionData.variantId);
      this.regimenService.invalidateRegimenCache();
    } catch (err) {
      // Handle error
    }
  }

  // Note: This block is for 2999 variant (variant ID: variant_4999) mandatory call
  // Please remove this once backend solution is implemented
  // If the user has opted for doctor call, no change needed
  async handleMandatoryVariantCall(): Promise<void> {
    try {
      const mandatoryVariantCall = this.regimenNewPropositionData.variantId === VARIANT_ID.variant_4999;
      if (!this.optedForDoctorCall) {
        await this.conn.optForRegimenDoctorCall(this.regimenNewPropositionData.regimenId, mandatoryVariantCall);
        this.optedForDoctorCall = mandatoryVariantCall;
        if (mandatoryVariantCall) {
          this.user.set('hasUserOptedForCall', false);
          await this.user.save();
        }
      } else {
        const hasUserOptedForCall = this.user?.get('hasUserOptedForCall') || false;
        const is999Variant = this.regimenNewPropositionData.variantId === VARIANT_ID.variant_999;
        if ((!hasUserOptedForCall && !mandatoryVariantCall) || is999Variant) {
          await this.conn.optForRegimenDoctorCall(this.regimenNewPropositionData.regimenId, false);
          this.optedForDoctorCall = false;
        }
      }
    } catch (err) {
      // Handle error
    }
  }

  async updateRegimenWithSelectedPlan(selectedPlan: PlanName): Promise<void> {
    if (this.updatingPlan) return;
    const variantIdToUpdate = selectedPlanAndVariantIdMap[selectedPlan] || selectedPlanAndVariantIdMap.advance;
    this.updatingPlan = true;
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'treatment-info-select-plan', variant: variantIdToUpdate }), [
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    const planPrice = this.regimenPriceBasedOnSelectedPlan;
    await this.updateRegimenVariant(variantIdToUpdate);
    this.selectedRegimen.selectedVariantId = variantIdToUpdate;
    this.showToast(planPrice);
  }

  private async updateRegimenVariant(variantIdToUpdate: string): Promise<void> {
    try {
      await this.conn.updateRegimenInfo(this.selectedRegimen.regimenId, variantIdToUpdate);
      if ((this.selectedRegimen as any)?.extraProducts?.length) {
        const extraProductIds: string[] = (this.selectedRegimen as any)?.extraProducts?.map((product: any): string => product?.objectId) || [];
        await this.conn.updateRegimenPrice({ regimenId: this.selectedRegimen.regimenId, extraProductIds });
      }
      this.regimenService.invalidateRegimenCache();
    } finally {
      this.updatingPlan = false;
    }
  }


  private showToast(price: number): void {
    this.toastPlanPrice = price;
    this.showPlanSelectedToast = true;
    this.isToastDismissing = false;
    if (this.toastTimeoutId) clearTimeout(this.toastTimeoutId);
    this.toastTimeoutId = setTimeout(() => {
      this.isToastDismissing = true;
    }, 2000);
  }

  onToastAnimationEnd(): void {
    if (this.isToastDismissing) {
      this.showPlanSelectedToast = false;
      this.isToastDismissing = false;
    }
  }

  private dismissToastImmediately(): void {
    if (this.toastTimeoutId) clearTimeout(this.toastTimeoutId);
    this.toastTimeoutId = null;
    this.showPlanSelectedToast = false;
    this.isToastDismissing = false;
  }

  ngOnDestroy(): void {
    if (this.toastTimeoutId) clearTimeout(this.toastTimeoutId);
  }

  buyRegimen(): void {
    if (this.updatingPlan) return;
    this.eventLoggerService.cleverTapEvent('buyNowRegimen', JSON.stringify({ regimenId: this.selectedRegimen?.regimenId }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.eventLoggerService.trackInParse('BUY_NOW', ApiClientConstant?.Event?.Type?.ORDER);
    this.eventLoggerService.trackEvent('BUY_NOW_CLICKED');
    this.eventLoggerService.trackInFirebaseAndBranch('BUY_NOW_CLICKED');
    if (this.regimenClass === this.appConfig.Shared.Regimen.Class.HAIR) {
      this.eventLoggerService.trackEvent('BUY_NOW_CLICKED_HAIR');
      this.eventLoggerService.trackInFirebaseAndBranch('BUY_NOW_CLICKED_HAIR');
    }
    const orderState = this.user?.get('orderState');
    this.router.navigate(['/user/checkout'], { queryParams: { type: 'REGIMEN', regimenId: this.selectedRegimen?.regimenId, useCureSkinCash: String(this.experimentService.shouldUseCureSkinCash(orderState)) } });
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: 'BUY_NOW_CLICKED' },
      firebase: { name: 'BUY_NOW_CLICKED' },
    });
  }

  get shouldShowRebrandedRegimenProposition(): boolean {
    return this.regimenService.shouldShowRebrandedRegimenPropositionUI(this.regimenClass);
  }
}
